import {
  Box,
  Button,
  Grid,
  Paper,
  styled,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  Tooltip,
  Chip,
} from "@mui/material";
import { IOpsLinkMessage, IOpsLinkTopic, IFlight } from "@vmosolutions/oc-interfaces";
import { FlightChip } from "src/components/Flights/FlightChip";
import { useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import moment from "moment-timezone";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { acknowledgeEvent } from "src/redux/reducers/OpsLinkReducer";
import { useRetrieveUserName } from "./shared/useRetrieveUserName";
import { buildAcknowledgementTooltip } from "./shared/acknowledgementTooltip";
import { getAcknowledgementState, IAcknowledgementState } from "./shared/acknowledgementState";
import { isHumanWriteableTopic } from "./shared/humanWriteableTopic";

interface IOpsLinkEventDisplay {
  id: string;
  currentEvent: IOpsLinkMessage;
  trackedFlights: IFlight[];
  setReplyEvent: React.Dispatch<React.SetStateAction<IOpsLinkMessage>>;
}

const OCOpsLinkEventDisplay = styled(Paper)(({ theme }) => ({
  padding: "5px",
  width: "100%",
  flex: "1 1 auto",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  fieldset: {
    border: "1px solid rgba(255, 255, 255, 0.23) !important",
  },
  "& .entryField": {
    input: {
      height: "2em !important",
      maxHeight: "2em !important",
      padding: "0px 2px 0px 10px",
    },
    "& .flightchip": {
      height: 22,
    },
  },
}));

const getAcknowledgeButtonColor = (state: IAcknowledgementState): "success" | "inherit" | "info" | "primary" => {
  if (!state.isFromCrew) return state.hasBeenAcknowledged ? "success" : "inherit";
  if (state.currentUserAcknowledged) return "success";
  return state.hasBeenAcknowledged ? "info" : "primary";
};

const getAcknowledgeButtonIcon = (state: IAcknowledgementState): JSX.Element => {
  if (!state.isFromCrew) return state.hasBeenAcknowledged ? <CheckCircleIcon /> : <RemoveCircleOutlineIcon />;
  if (state.currentUserAcknowledged) return <CheckCircleIcon />;
  return <CheckCircleOutlineIcon />;
};

const getAcknowledgeButtonLabel = (state: IAcknowledgementState): string => {
  if (state.role === "own") return state.hasBeenAcknowledged ? `Acknowledged (${state.count})` : "Sent by you";
  if (state.role === "dispatcher")
    return state.hasBeenAcknowledged ? `Acknowledged (${state.count})` : "Awaiting crew acknowledgement";
  if (state.currentUserAcknowledged) return `You Acknowledged${state.count > 1 ? ` (+${state.count - 1})` : ""}`;
  if (state.hasBeenAcknowledged) return `Acknowledge (${state.count} ack'd)`;
  return "Acknowledge";
};

export default function OpsLinkEventDisplay(props: IOpsLinkEventDisplay): JSX.Element {
  const { id, currentEvent, trackedFlights, setReplyEvent } = props;
  const dispatch = useDispatch();
  const currentUserId = useSelector((state: IRootState) => state.authentication.attributes?._id);
  const getAckUserName = useRetrieveUserName();

  const topic = currentEvent.topic as IOpsLinkTopic;
  const supportsAcknowledgement = topic?.acknowledgeTracking === true;
  const canReply = isHumanWriteableTopic(currentEvent.topic);
  const ackState = getAcknowledgementState(currentEvent, currentUserId);
  const { role, isFromCrew, hasBeenAcknowledged, currentUserAcknowledged } = ackState;

  const handleAcknowledge = useCallback(() => {
    const flightId = typeof currentEvent.flight === "string" ? currentEvent.flight : currentEvent.flight._id;
    dispatch(acknowledgeEvent({ flightId, messageId: currentEvent._id }));
  }, [currentEvent, dispatch]);

  const chipData = useMemo(() => {
    const flight = trackedFlights.find((trackedFlight) => trackedFlight._id === currentEvent.flight);
    return flight;
  }, [currentEvent, trackedFlights]);

  const startLocal = useMemo(() => {
    if (!chipData) return null;
    return chipData.actualOut && chipData.originAirport?.timezoneId
      ? moment.utc(chipData.actualOut).tz(chipData.originAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
      : null;
  }, [chipData]);

  const endLocal = useMemo(() => {
    if (!chipData) return null;
    return chipData.actualIn && chipData.destAirport?.timezoneId
      ? moment.utc(chipData.actualIn).tz(chipData.destAirport?.timezoneId)?.format("MMM Do, Y HH:mm z")
      : null;
  }, [chipData]);

  // For an unacknowledged crew message the button is actionable, so keep the original
  // call-to-action hint here rather than the shared status text.
  const acknowledgementTooltip =
    role === "crew" && !hasBeenAcknowledged
      ? "Acknowledge this message"
      : buildAcknowledgementTooltip(role, currentEvent.acknowledgedBy ?? [], getAckUserName);

  const acknowledgeButtonVariant =
    currentUserAcknowledged || !isFromCrew || hasBeenAcknowledged ? "outlined" : "contained";

  return (
    <OCOpsLinkEventDisplay elevation={1}>
      <Grid container spacing={1} sx={{ width: "100%", margin: "0", flexShrink: 0 }}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel sx={{ backgroundColor: "#263647", padding: "0 5px" }} shrink={true} id="flightLabel">
              Flight
            </InputLabel>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                textAlign: "center",
                width: "100%",
                borderRadius: "4px",
                paddingRight: "5px",
                border: "1px solid rgba(255, 255, 255, 0.23) !important",
              }}
              id="flight"
              className="entryField"
            >
              {chipData && (
                <Box
                  sx={{
                    zIndex: 10,
                    display: "flex",
                    flexWrap: "nowrap",
                    overflow: "hidden",
                    padding: "2px 2px 2px 10px",
                  }}
                >
                  <FlightChip data={chipData} />
                  <Box
                    sx={{
                      display: "flex",
                      flexGrow: 1,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ padding: "3px 5px 0px 5px", textWrap: "nowrap", overflow: "hidden" }}
                    >
                      {startLocal}
                    </Typography>
                    <ArrowRightAltIcon />
                    <Typography
                      variant="body2"
                      sx={{ paddingTop: "3px", paddingLeft: "5px", textWrap: "nowrap", overflow: "hidden" }}
                    >
                      {endLocal}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            disabled
            //@ts-ignore
            value={currentEvent.topic.shortName}
            size="small"
            variant="outlined"
            id="topic"
            className="entryField"
            name="topic"
            label="Topic"
          />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            disabled
            value={currentEvent.eventType}
            size="small"
            variant="outlined"
            id="eventType"
            name="eventType"
            className="entryField"
            label="Subject"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            disabled
            value={currentEvent.priority}
            size="small"
            variant="outlined"
            id="priority"
            className="entryField"
            name="priority"
            label="Priority"
          />
        </Grid>
      </Grid>
      <Box sx={{ flexGrow: 1, minHeight: "56px", display: "flex", flexDirection: "column", mt: 1, px: "4px" }}>
        <TextField
          fullWidth
          disabled
          value={currentEvent.text}
          multiline
          variant="outlined"
          id="text"
          name="text"
          label="Text"
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            "& .MuiInputBase-root": {
              flex: 1,
              minHeight: 0,
              alignItems: "stretch",
            },
            "& .MuiInputBase-inputMultiline": {
              height: "100% !important",
              overflow: "auto !important",
              boxSizing: "border-box",
            },
          }}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, px: "4px" }}>
        {canReply && (
          <Button
            size="small"
            onClick={() => setReplyEvent(currentEvent)}
            variant="contained"
            sx={{ marginLeft: "5px" }}
          >
            Reply
          </Button>
        )}
        {supportsAcknowledgement && (
          <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{acknowledgementTooltip}</Box>}>
            <span>
              <Button
                size="small"
                onClick={handleAcknowledge}
                disabled={currentUserAcknowledged || !isFromCrew}
                variant={acknowledgeButtonVariant}
                color={getAcknowledgeButtonColor(ackState)}
                startIcon={getAcknowledgeButtonIcon(ackState)}
              >
                {getAcknowledgeButtonLabel(ackState)}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </OCOpsLinkEventDisplay>
  );
}
